/**
 * How prose may be dressed: the colours, highlights and sizes a word can wear,
 * the alignments a paragraph can take, and how a block of text is set as a
 * whole.
 *
 * All of it travels as class names, never as inline style. The sanitiser
 * strips `style` on purpose (see `sanitise.ts`), so a colour typed into a
 * paragraph has to be one the stylesheet knows: a short palette here, drawn
 * in `builder.css` under `.page`, for the page, the reader and the export
 * alike. A palette rather than any colour is also what keeps a report in its
 * house's colours.
 *
 * Pure: names and class names, and reading stored settings forgivingly.
 */

import { readBoolean, readNumber, readString, type JsonValue } from '@/domain/json'

/** The fixed inks: the document's accent, its ink, and a short range. */
export const BASE_INKS = [
  'accent',
  'ink',
  'grey',
  'red',
  'orange',
  'green',
  'teal',
  'blue',
  'violet',
] as const
export type BaseInk = (typeof BASE_INKS)[number]

/**
 * The charter's own colours, by their place in the document's palette
 * (`layout.palette`): as many as it may hold (`MAX_PALETTE`). By place rather
 * than by value, so a word set in the charter's second colour follows the
 * charter when that colour changes.
 */
export const HOUSE_INKS = ['house1', 'house2', 'house3', 'house4', 'house5'] as const
export type HouseInk = (typeof HOUSE_INKS)[number]

/** Every ink a word can be set in. */
export const PROSE_INKS = [...BASE_INKS, ...HOUSE_INKS] as const
export type ProseInk = (typeof PROSE_INKS)[number]

/** What a word can be highlighted with. */
export const PROSE_MARKS = ['yellow', 'green', 'blue', 'pink', 'grey'] as const
export type ProseMark = (typeof PROSE_MARKS)[number]

/** A word set smaller or larger than the text around it. */
export const PROSE_SIZES = ['small', 'large', 'xlarge'] as const
export type ProseSize = (typeof PROSE_SIZES)[number]

/** How a paragraph lines up. */
export const PROSE_ALIGNS = ['left', 'center', 'right', 'justify'] as const
export type ProseAlign = (typeof PROSE_ALIGNS)[number]

/** The colours the palette shows, to draw its swatches: the stylesheet has the same. */
export const INK_SWATCHES: Record<Exclude<BaseInk, 'accent' | 'ink'>, string> = {
  grey: '#6b7280',
  red: '#b3261e',
  orange: '#c2410c',
  green: '#15803d',
  teal: '#0f766e',
  blue: '#1d4ed8',
  violet: '#6d28d9',
}

export const MARK_SWATCHES: Record<ProseMark, string> = {
  yellow: '#fff1a8',
  green: '#d3f2dc',
  blue: '#d6e6ff',
  pink: '#fbd9e6',
  grey: '#e8eaee',
}

/** The class each dressing is written as, and read back from. */
export const PROSE_CLASS = {
  ink: (name: ProseInk) => `ink-${name}`,
  mark: (name: ProseMark) => `mark-${name}`,
  size: (name: ProseSize) => `size-${name}`,
  align: (name: ProseAlign) => `align-${name}`,
} as const

/** The name a class carries for a family of dressing, if it is one of that family's. */
export function proseClassValue<T extends string>(
  prefix: 'ink' | 'mark' | 'size' | 'align',
  names: readonly T[],
  className: string,
): T | null {
  if (!className.startsWith(`${prefix}-`)) return null
  const name = className.slice(prefix.length + 1)
  return (names as readonly string[]).includes(name) ? (name as T) : null
}

// ---------------------------------------------------------------------------
// A block of text, set as a whole
// ---------------------------------------------------------------------------

export const TEXT_COLUMNS = [1, 2, 3] as const
export type TextColumns = (typeof TEXT_COLUMNS)[number]

export const COLUMN_GAPS = ['narrow', 'normal', 'wide'] as const
export type ColumnGap = (typeof COLUMN_GAPS)[number]

/** The block's alignment; `document` follows the report's own setting. */
export const BLOCK_ALIGNS = ['document', 'left', 'justify', 'center'] as const
export type BlockAlign = (typeof BLOCK_ALIGNS)[number]

/** Line spacing; `document` is the report's own. */
export const LEADINGS = ['document', 'tight', 'normal', 'loose'] as const
export type Leading = (typeof LEADINGS)[number]

/** A frame around the block: a tint behind it, a line round it, or the accent's tint. */
export const TEXT_FRAMES = ['none', 'tint', 'outline', 'accent'] as const
export type TextFrame = (typeof TEXT_FRAMES)[number]

/** How a block of text is set, beyond its words. */
export interface TextSetting {
  readonly columns: TextColumns
  readonly columnGap: ColumnGap
  /** A hairline between the columns. */
  readonly columnRule: boolean
  readonly align: BlockAlign
  /** Words broken at the end of a line where the language allows. */
  readonly hyphens: boolean
  readonly leading: Leading
  readonly frame: TextFrame
  /** The first letter set large across the first lines. */
  readonly dropCap: boolean
}

export const DEFAULT_TEXT_SETTING: TextSetting = {
  columns: 1,
  columnGap: 'normal',
  columnRule: false,
  align: 'document',
  hyphens: true,
  leading: 'document',
  frame: 'none',
  dropCap: false,
}

function oneOf<T extends string>(options: readonly T[], value: string, fallback: T): T {
  return (options as readonly string[]).includes(value) ? (value as T) : fallback
}

/** A block's setting read back off a stored section, field by field; anything unknown is the default. */
export function readTextSetting(stored: JsonValue | undefined): TextSetting {
  const columns = readNumber(stored, 'columns', 1)
  return {
    columns: columns === 2 || columns === 3 ? columns : 1,
    columnGap: oneOf(COLUMN_GAPS, readString(stored, 'columnGap'), 'normal'),
    columnRule: readBoolean(stored, 'columnRule'),
    align: oneOf(BLOCK_ALIGNS, readString(stored, 'align'), 'document'),
    hyphens: readBoolean(stored, 'hyphens', true),
    leading: oneOf(LEADINGS, readString(stored, 'leading'), 'document'),
    frame: oneOf(TEXT_FRAMES, readString(stored, 'frame'), 'none'),
    dropCap: readBoolean(stored, 'dropCap'),
  }
}

const GAPS: Record<ColumnGap, string> = { narrow: '6mm', normal: '10mm', wide: '15mm' }
const LEADING_VALUES: Record<Exclude<Leading, 'document'>, string> = {
  tight: '1.3',
  normal: '1.5',
  loose: '1.75',
}

/**
 * How a block is set, as the variables and classes its markup carries: the
 * same on the page while it is read and while it is written.
 */
export function textSettingStyle(setting: TextSetting): Record<string, string> {
  return {
    '--text-columns': String(setting.columns),
    '--text-gap': GAPS[setting.columnGap],
    '--text-rule': setting.columnRule ? '0.5pt solid var(--paper-rule)' : 'none',
    ...(setting.align !== 'document' ? { '--page-align': setting.align } : {}),
    '--text-hyphens': setting.hyphens ? 'auto' : 'manual',
    ...(setting.leading !== 'document'
      ? { '--text-leading': LEADING_VALUES[setting.leading] }
      : {}),
  }
}

export function textSettingClasses(setting: TextSetting): string[] {
  return [
    ...(setting.frame !== 'none' ? [`text-frame text-frame--${setting.frame}`] : []),
    ...(setting.dropCap ? ['text-drop-cap'] : []),
  ]
}
